import { defineNuxtPlugin } from '@nuxtjs/composition-api';
import mitt from 'mitt';
import {
  reactive,
  set,
} from 'vue';

import type { Component } from 'vue';

interface OpenOptions {
  component: Component | string | undefined;
  overlay?: boolean;
  floatingButton?: boolean;
  duration?: number;
  fullScreen?: boolean;
  preventScroll?: boolean;
  transition?: unknown;
  events?: Array<{ value: string; callback(...args: Array<unknown>): unknown }>;
  attrs?: Record<string, unknown> | undefined;
  onClose?: () => void;
  scrollbar?: null;
  keyReloadsPage: number;
}

type PublicOpenOptions = Omit<OpenOptions, 'keyReloadsPage'>;

interface ModalPlugin {
  open(options: OpenOptions): void;
  close(): void;
  change(prop: string, value: unknown): void;
  changeAttrs(prop: string, value: unknown): void;
  increaseKey(): void;
  getKeyReloadsPage(): number;
}

// mitt хочет type
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type Events = {
  'open': PublicOpenOptions;
  'close': undefined;
  'modal:beforeClose': undefined;
  'modal:closed': undefined;
  'change': undefined;
};

const modal = {
  data: reactive<OpenOptions>({
    component: undefined,
    attrs: undefined,
    onClose: undefined,
    transition: '',
    overlay: true,
    duration: 500,
    scrollbar: null,
    floatingButton: false,
    fullScreen: false,

    keyReloadsPage: 2,
  }),

  event: mitt<Events>(),

  open (options: PublicOpenOptions): void {
    this.event.emit('open', options);
  },

  close (): void {
    this.event.emit('close');
  },

  change (prop: string, value: unknown): void {
    set(this.data, prop, value);
  },

  changeAttrs (prop: string, value: unknown): void {
    if (this.data.attrs) {
      set(this.data.attrs, prop, value);
    }
  },

  // Используется для обновления страниц
  // Например, на странице делаем watch на keyReloadsPage
  // и при изменении keyReloadsPage вызываем fetch
  increaseKey (): void {
    set(this.data, 'keyReloadsPage', this.data.keyReloadsPage + 1);
  },

  getKeyReloadsPage (): number {
    return this.data.keyReloadsPage;
  },
};

export default defineNuxtPlugin(({ app }, inject) => {
  app.$modal = modal;
  inject('modal', modal);
});

declare module 'vue/types/vue' {
  interface Vue {
    $modal: typeof modal;
  }
}

export function useModalHandler (): typeof modal {
  return modal;
}
